// Store rows under the seller sidebar's "Awaiting Shipment" (ShipStation
// parity): every connected shop with its awaiting-shipment count.
//
// The counts come from the whole-org summary (GET /seller/orders/summary →
// awaiting_shipment_by_store), never from a loaded list page, so they add up
// to the Awaiting Shipment badge above them.

import type { SidebarSubItem } from '../../components/Sidebar'
import type { SellerStoreRow } from '../../api/seller-listings'

export type AwaitingShipmentStoreCount = {
  store_integration_id: string | null
  store_origin: string
  count: number
}

const ORIGIN_PREFIX = 'origin:'

/** The value the My Orders store filter keys on — the sidebar links and the
 * list MUST derive it the same way or a store row would open an empty list.
 *
 * A shop id when the order belongs to a shop the seller still has; otherwise
 * the provider, so orders with no shop (or from a removed one) keep a filter of
 * their own instead of vanishing from every store view. */
export function storeFilterKey(
  storeIntegrationId: string | null | undefined,
  storeOrigin: string,
  knownStoreIds: ReadonlySet<string>,
): string {
  return storeIntegrationId && knownStoreIds.has(storeIntegrationId)
    ? storeIntegrationId
    : `${ORIGIN_PREFIX}${storeOrigin}`
}

function providerLabel(provider: string): string {
  if (provider === 'ebay') return 'eBay'
  return provider.charAt(0).toUpperCase() + provider.slice(1)
}

export function awaitingShipmentStoreLink(key: string): string {
  return `/seller/my-orders?status=awaiting_shipment&store=${encodeURIComponent(key)}`
}

/** Sidebar leaves: the seller's shops by name (listed even with nothing
 * waiting), then one "Other <provider> orders" row per provider for orders with
 * no shop or from a removed one — so the rows always add up to the badge. */
export function buildAwaitingShipmentStoreItems(
  stores: SellerStoreRow[],
  counts: AwaitingShipmentStoreCount[],
  otherLabel: OtherOrdersLabel = defaultOtherOrdersLabel,
): SidebarSubItem[] {
  const knownStoreIds = new Set(stores.map((s) => s.id))
  const countByKey = new Map<string, number>()
  for (const c of counts) {
    const key = storeFilterKey(c.store_integration_id, c.store_origin, knownStoreIds)
    countByKey.set(key, (countByKey.get(key) ?? 0) + c.count)
  }

  const shops = stores
    // A deactivated shop stays listed only while it still has orders to ship.
    .filter((s) => s.active !== false || (countByKey.get(s.id) ?? 0) > 0)
    .slice()
    .sort((a, b) => a.shop_name.localeCompare(b.shop_name, undefined, { numeric: true }))
  const items: SidebarSubItem[] = shops.map((shop) => ({
    to: awaitingShipmentStoreLink(shop.id),
    label: shop.shop_name || providerLabel(shop.provider),
    badge: countByKey.get(shop.id) ?? 0,
  }))

  const others = [...countByKey.entries()]
    .filter(([key, count]) => key.startsWith(ORIGIN_PREFIX) && count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
  for (const [key, count] of others) {
    items.push({ to: awaitingShipmentStoreLink(key), label: otherOrdersLabel(key, otherLabel), badge: count })
  }
  return items
}

/** Builds the "Other <provider> orders" row label; callers pass a localized
 * version (the English default keeps non-localized callers unchanged). */
export type OtherOrdersLabel = (provider: string) => string

const defaultOtherOrdersLabel: OtherOrdersLabel = (provider) => `Other ${provider} orders`

function otherOrdersLabel(key: string, label: OtherOrdersLabel): string {
  return label(providerLabel(key.slice(ORIGIN_PREFIX.length)))
}

/** Store filter menu options, in the same order and with the same names as the
 * sidebar rows: shops by name (deactivated ones only while they still have
 * orders), then an "Other <provider> orders" option per fallback key present. */
export function storeFilterOptions(
  stores: SellerStoreRow[],
  orderStoreKeys: Iterable<string>,
  otherLabel: OtherOrdersLabel = defaultOtherOrdersLabel,
): { key: string; label: string }[] {
  const present = new Set(orderStoreKeys)
  const shops = stores
    .filter((s) => s.active !== false || present.has(s.id))
    .slice()
    .sort((a, b) => a.shop_name.localeCompare(b.shop_name, undefined, { numeric: true }))
  return [
    ...shops.map((shop) => ({ key: shop.id, label: shop.shop_name || providerLabel(shop.provider) })),
    ...[...present]
      .filter((key) => key.startsWith(ORIGIN_PREFIX))
      .sort()
      .map((key) => ({ key, label: otherOrdersLabel(key, otherLabel) })),
  ]
}
